import React, { useContext } from 'react';

import { Calendar } from 'primereact/calendar';
import { MultiSelect } from 'primereact/multiselect';
import { Tooltip } from 'primereact/tooltip';
import { Dropdown } from 'primereact/dropdown';
import moment from 'moment';
import useFilterBreakpoint from 'hooks/useFilterBreakpoint';
import { Button } from 'primereact/button';

import { useSecureMessageContext } from '../../Context/SecureMessageContext';
import { UserDetailsContext, useUserDetailsContext } from 'context/userDetailsContext';
import { convertToTimezoneWithStartOfDayFormat, convertToTimezoneWithEndOfDayFormat } from 'utils/utility_functions/timezone';
import { msgStatusOptions, participantOptions, sentimentOptions } from 'constants/dropdownOptions';
import CaseListDropDown from 'components/CaseListDropDown/CaseListDropDown';
import TextSnippetWrapper from 'components/TextSnippetWrapper';
import { LoaderComponent } from 'shared/LoaderComponent';
import { handleFilterUpdateInUserContext } from 'common/pagination';
import { assignToItemTemplate } from 'modules/Tasks/components/TaskBoard/Board/PriorityDropdownTemplate';

function FilterComponent() {
  let {
    isGlobalMessagesScreen,
    fetchCaseMessage,
    authorsList,
    clientsList,
    filterCaseId,
    setFilterCaseId,
    filterMsgStatus,
    setFilterMsgStatus,
    filterAuthorId,
    setFilterAuthorId,
    dateForFilter,
    setDateForFilter,
    searchKeyWord,
    setSearchKeyWord,
    refreshCase,
    isFromClientPortal,
    filterSentiment,
    setFilterSentiment,
    filterParticipant,
    setFilterParticipant,
    isAuthorLoading,
  } = useSecureMessageContext();

  const { userDetails } = useUserDetailsContext();
  const userContext = useContext(UserDetailsContext);

  const pickExtractedDate = (dateArray) => {
    return {
      message_date: dateArray?.[0] ? convertToTimezoneWithStartOfDayFormat(dateArray[0], userDetails.timezone) : '',
      to_limit: dateArray?.[0] ? convertToTimezoneWithEndOfDayFormat(dateArray[1] ? dateArray[1] : dateArray[0], userDetails.timezone) : '',
    };
  };

  const searchMessage = () => {
    let authorArr = filterSendAuthorID(filterAuthorId);

    let obj = {
      case_id: filterCaseId,
      author_id: authorArr,
      is_read: filterMsgStatus,
      keyword: searchKeyWord,
      participant: filterParticipant,
      sentiment_filter: filterSentiment,
      ...pickExtractedDate(dateForFilter),
    };
    fetchCaseMessage(obj);
  };

  const searchByTitle = (keyWord) => {
    setSearchKeyWord(keyWord);
  };

  const filterByCase = (e) => {
    setFilterCaseId(e);
    let authorArr = filterSendAuthorID(filterAuthorId);
    let obj = {
      case_id: e?.id ? e?.id : '',
      author_id: authorArr,
      is_read: filterMsgStatus,
      keyword: searchKeyWord,
      participant: filterParticipant,
      sentiment_filter: filterSentiment,
      ...pickExtractedDate(dateForFilter),
    };
    fetchCaseMessage(obj);
    if (isGlobalMessagesScreen) {
      handleFilterUpdateInUserContext(userContext, 'filters', 'secure_messages', null, 'case', e, true);
    }
  };

  const filterByMsgStatus = (e) => {
    let statusVal = e.value === 'All' ? '' : e.value;
    setFilterMsgStatus(statusVal);

    let authorArr = filterSendAuthorID(filterAuthorId);
    let obj = {
      case_id: filterCaseId,
      author_id: authorArr,
      keyword: searchKeyWord,
      participant: filterParticipant,
      sentiment_filter: filterSentiment,
      ...pickExtractedDate(dateForFilter),
    };
    if (statusVal === 'scheduled') {
      obj.scheduled_status = statusVal;
    } else {
      obj.is_read = statusVal;
    }
    fetchCaseMessage(obj);
    if (isGlobalMessagesScreen) {
      handleFilterUpdateInUserContext(userContext, 'filters', 'secure_messages', null, 'message_status', e?.value, true);
    }
  };
  const filterByAuthor = (e) => {
    setFilterAuthorId(e.value);
    let authorArr = filterSendAuthorID(e.value);
    let obj = {
      case_id: filterCaseId,
      author_id: authorArr,
      is_read: filterMsgStatus,
      keyword: searchKeyWord,
      participant: filterParticipant,
      sentiment_filter: filterSentiment,
      ...pickExtractedDate(dateForFilter),
    };
    fetchCaseMessage(obj);
    if (isGlobalMessagesScreen) {
      handleFilterUpdateInUserContext(userContext, 'filters', 'secure_messages', null, 'author', e?.value, true);
    }
  };

  const filterByDate = (e) => {
    if (e.value) {
      setDateForFilter(e.value);
      let authorArr = filterSendAuthorID(filterAuthorId);
      let obj = {
        case_id: filterCaseId,
        author_id: authorArr,
        is_read: filterMsgStatus,
        keyword: searchKeyWord,
        participant: filterParticipant,
        sentiment_filter: filterSentiment,
        ...pickExtractedDate(e.value),
      };
      fetchCaseMessage(obj);
    } else {
      setDateForFilter('');
      fetchCaseMessage({});
    }
    if (isGlobalMessagesScreen) {
      handleFilterUpdateInUserContext(userContext, 'filters', 'secure_messages', null, 'date', e?.value || [], true);
    }
  };

  const filterSendAuthorID = (arr = []) => {
    try {
      if (arr && arr.length) {
        let clientCognitoUsername = clientsList
          ? clientsList
              .filter((val) => val?.client_cognito_username || val?.user_group_id)
              .map((v) => v?.client_cognito_username || v?.contact_id || v?.user_group_id)
          : [];
        let dat = arr.filter((val) => clientCognitoUsername.includes(val));
        return dat;
      } else {
        return [];
      }
    } catch (err) {
      return [];
    }
  };

  const filterChange = (e, type) => {
    if (type === 'sentiment') {
      setFilterSentiment(e.value);
      if (isGlobalMessagesScreen) {
        handleFilterUpdateInUserContext(userContext, 'filters', 'secure_messages', null, 'sentiment', e?.value, true);
      }
    } else if (type === 'participants') {
      setFilterParticipant(e.value);
      if (isGlobalMessagesScreen) {
        handleFilterUpdateInUserContext(userContext, 'filters', 'secure_messages', null, 'participants', e?.value, true);
      }
    }

    let authorArr = filterSendAuthorID(filterAuthorId);
    let obj = {
      case_id: filterCaseId,
      author_id: authorArr,
      is_read: filterMsgStatus,
      keyword: searchKeyWord,
      participant: type === 'participants' ? e.value : filterParticipant,
      sentiment_filter: type === 'sentiment' ? e.value : filterSentiment,
      ...pickExtractedDate(dateForFilter),
    };
    fetchCaseMessage(obj);
  };

  const { showFilters, setShowFilters } = useFilterBreakpoint();

  return (
    <div className="d-flex flex-wrap align-items-center filter-wrap mb-3 p-2 w-100 align-items-center justify-content-between gap-2">
      {isGlobalMessagesScreen && (
        <div className="filter-wraper">
          <span className="p-input-icon-right input-shadow w-100">
            <i className="icon-search F-size16 cursor-pointer" onClick={searchMessage} />
            <TextSnippetWrapper
              type="text"
              placeholder="Search"
              value={searchKeyWord}
              onChange={(e) => {
                searchByTitle(e.target.value);
              }}
              onKeyPress={(event) => {
                if (event.key === 'Enter') {
                  searchMessage();
                }
              }}
              className="input-search"
              autoComplete="off"
            />
          </span>
        </div>
      )}
      <div className="d-block d-lg-none w-100">
        <Button
          className="p-button p-component btn p-button-secondary outline w-100 filter-toggle-btn"
          type="button"
          onClick={() => setShowFilters((v) => !v)}
          aria-expanded={showFilters}
          aria-controls="msg-filter-section"
          label="Filters"
        >
          <span
            style={{
              fontSize: 18,
              transition: 'transform 0.2s',
              display: 'inline-block',
              transform: showFilters ? 'rotate(180deg)' : 'rotate(0deg)',
            }}
          >
            <i className="fa fa-solid fa-angle-down"></i>
          </span>
        </Button>
      </div>
      <div
        className={`d-flex flex-wrap align-items-center gap-2 filter-toggle-section filter-section${showFilters ? ' show' : ''}`}
        id="msg-filter-section"
        aria-hidden={!showFilters}
      >
        <label className="text-bold black-600  me-2 filter-label">Filters:</label>
        {isGlobalMessagesScreen && !isFromClientPortal && (
          <div className="d-flex flex-column filter-wraper">
            <CaseListDropDown
              isClient={isFromClientPortal}
              value={filterCaseId}
              onChange={(e) => {
                if (filterCaseId !== e) {
                  filterByCase(e);
                }
              }}
              placeholder="By Case/Lead"
              noOptionsMessage="No Cases/Leads Found"
              name="case"
              isClearable
              excludeFromLoading={true}
            />
          </div>
        )}
        {isGlobalMessagesScreen && isFromClientPortal && userContext?.clientCaseList.length > 1 && (
          <div className="d-flex flex-column filter-wraper">
            <CaseListDropDown
              isClient={isFromClientPortal}
              value={filterCaseId}
              onChange={(e) => {
                if (filterCaseId !== e) {
                  filterByCase(e);
                }
              }}
              name="case"
              excludeFromLoading={true}
            />
          </div>
        )}
        <div className="d-flex flex-column filter-wraper">
          <MultiSelect
            placeholder="By Author"
            options={authorsList}
            className="input-shadow F-size14"
            filter
            filterPlaceholder="Search"
            display="chip"
            optionLabel="assignee_name"
            itemTemplate={assignToItemTemplate}
            value={clientsList?.length > 0 ? filterAuthorId : []}
            onChange={(e) => filterByAuthor(e)}
            optionGroupLabel="assignee_name"
            optionGroupChildren="items"
            optionValue="assignee_id"
            resetFilterOnHide
            panelFooterTemplate={isAuthorLoading ? LoaderComponent : null}
          />
        </div>
        <div className="d-flex flex-column filter-wraper">
          <Calendar
            id="basic"
            value={dateForFilter}
            onChange={(e) => filterByDate(e)}
            placeholder="By Date"
            className="cursor input-shadow by-date"
            monthNavigator
            yearNavigator
            selectionMode="range"
            yearRange="2010:2030"
            showButtonBar
          />
        </div>
        <div className="d-flex flex-column filter-wraper">
          <Dropdown
            value={filterMsgStatus}
            getCaseList
            options={isFromClientPortal ? msgStatusOptions.filter((opt) => opt.value !== 'scheduled') : msgStatusOptions}
            onChange={filterByMsgStatus}
            placeholder="By Message Status"
          />
        </div>
        {!isFromClientPortal && (
          <div className="d-flex flex-column filter-wraper">
            <Dropdown
              value={filterParticipant}
              getCaseList
              options={participantOptions}
              onChange={(e) => filterChange(e, 'participants')}
              placeholder="By Participants"
            />
          </div>
        )}

        {!isFromClientPortal && (
          <div className="d-flex flex-column filter-wraper">
            <Dropdown
              value={filterSentiment}
              getCaseList
              options={sentimentOptions}
              onChange={(e) => filterChange(e, 'sentiment')}
              placeholder="By Sentiment"
            />
          </div>
        )}
        <div className="pointer me-0 p-2">
          <i className="fas fa-sync-alt" onClick={refreshCase}></i>
          <Tooltip content="Reset Filter" position="top" target=".fa-sync-alt" showDelay={500} />
        </div>
      </div>
    </div>
  );
}

export default FilterComponent;
